import Phaser from 'phaser';
import {
  BankAction,
  CONTAINER_EQUIPMENT,
  CONTAINER_GROUND,
  ChatChannel,
  CombatStance,
  DEFAULT_STANCE,
  EffectId,
  EntityKind,
  ItemKind,
  ItemSlot,
  MELEE_RANGE,
  MessageKind,
  NO_TARGET,
  STANCES,
  ShopAction,
  SpellTarget,
  TILE_SIZE,
  TileType,
  TradeAction,
  applySnapshot,
  chebyshevDistance,
  isCombatStance,
  // `isDoor` do protocolo, e não uma cópia local: as duas pontas já concordam sobre quais
  // bytes são porta (`isBlocking` depende disso), e uma segunda lista divergiria no dia em
  // que um quarto estado de porta entrasse.
  isDoor as isDoorTile,
  itemType,
  type ChatMessagePacket,
  type CombatEvent,
  type CombatPacket,
  type DespawnPacket,
  type Direction,
  type EffectPacket,
  type EntityInfoPacket,
  type EntityState,
  type GroundItemsPacket,
  type InventoryPacket,
  type MapChunkPacket,
  type TileUpdatePacket,
  type RedirectPacket,
  type ServerMessagePacket,
  type SnapshotPacket,
  type StatsPacket,
  type Vocation,
  type WelcomePacket,
} from '@game/protocol';

import { buildCharacterAtlases } from '../art/characters.ts';
import { buildCreatureAtlases } from '../art/creatures.ts';
import { buildEffectTextures } from '../art/effects.ts';
import { buildItemIcons, itemTextureKey } from '../art/items.ts';
import { buildRarityHalos } from '../art/rarity.ts';
import { animateTileset, buildTileset } from '../art/tiles.ts';
import { InputController } from '../input/keyboard.ts';
import { TouchControls } from '../input/touch.ts';
import { WalkPath, findPathNear } from '../input/walk-path.ts';
import { ApiError, requestPlaySession } from '../net/api.ts';
import { GameConnection } from '../net/connection.ts';
import { Correction, Predictor, isOccupied } from '../net/prediction.ts';
import { CreatureSprite, layoutCreatureLabels } from '../render/creature-sprite.ts';
import { EffectLayer } from '../render/effects.ts';
import { spawnFloatingText } from '../render/floating-text.ts';
import { GroundItemLayer } from '../render/ground-items.ts';
import { PointerMarks } from '../render/pointer-marks.ts';
import { Terrain } from '../render/terrain.ts';
import { ChatPanel } from '../ui/chat-panel.ts';
import { AudioEngine, Sfx } from '../audio/audio.ts';
import { mountAudioControls } from '../ui/audio-controls.ts';
import { CommercePanel } from '../ui/commerce.ts';
import { ConnectionBanner } from '../ui/connection-banner.ts';
import {
  ContextMenu,
  LONG_PRESS_MS,
  LONG_PRESS_SLOP_PX,
  type ContextAction,
} from '../ui/context-menu.ts';
import { BestiaryPanel } from '../ui/bestiary.ts';
import { AutomationPanel } from '../ui/automation.ts';
import { Minimap } from '../ui/minimap.ts';
import { SocialPanel } from '../ui/social.ts';
import { HotkeyBar } from '../ui/hotkeys.ts';
import { Hud } from '../ui/hud.ts';
import { InventoryWindow } from '../ui/inventory.ts';
import { EquipmentPanel } from '../ui/equipment.ts';
import {
  clearDropHighlight,
  dropTargetAt,
  highlightDropTarget,
  setItemIconResolver,
} from '../ui/slot.ts';
import { dragGhost, type DragGhost } from '../ui/drag-ghost.ts';

/** Intervalo entre pings. Mede latência sem virar tráfego relevante. */
const PING_INTERVAL_MS = 1000;

/**
 * Enquadramento pretendido, em tiles.
 *
 * O tile tem 32 px, e a 100% de zoom uma tela de 1440 px mostraria 45 tiles — o
 * personagem vira uma formiga e a arte de 32×32, que é feita para ser lida de perto,
 * some. Os dois eixos entram na conta porque só a largura **erra em tela larga**: numa
 * ultrawide em tela cheia, o cálculo pela horizontal pede um zoom altíssimo e fecha o
 * campo vertical, que é justamente o eixo curto. O zoom sai do eixo mais apertado.
 */
const TILES_ACROSS = 26;
const TILES_DOWN = 13;

/** Limites e passo do zoom. Ver `applyZoom`. */
const ZOOM_MIN = 1;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.5;

/** Onde a preferência de zoom do jogador sobrevive ao recarregamento. */
const ZOOM_STORAGE_KEY = 'hexaria.zoom';

/**
 * Onde a postura de combate escolhida sobrevive ao recarregamento.
 *
 * No navegador e não no banco, pela mesma razão do zoom e dos painéis recolhidos (ver
 * `ui/panel.ts`): é preferência de quem joga, e não estado do personagem. O servidor põe todo
 * mundo em `Balanced` na entrada, e é o cliente que reenvia a escolha a cada Welcome — o que
 * também cobre a travessia de portal, onde quem recebe o personagem é um worker que nunca
 * ouviu falar dele.
 */
const STANCE_STORAGE_KEY = 'hexaria.postura';

const COLOR_DAMAGE_DEALT = '#f2f2f2';
const COLOR_DAMAGE_TAKEN = '#ff6b6b';
const COLOR_MISS = '#8fa3b5';

/**
 * Quanto o ponteiro precisa andar para um clique no cenário virar arraste.
 *
 * O mesmo número do toque longo, e de propósito: os três gestos que nascem do mesmo
 * `pointerdown` — clicar, arrastar e segurar — se distinguem por movimento e por tempo, e dois
 * limiares diferentes de movimento fariam existir uma faixa em que o dedo já cancelou o menu
 * mas ainda não começou o arraste. Nessa faixa o gesto não seria nenhum dos três.
 */
const DRAG_SLOP_PX = LONG_PRESS_SLOP_PX;

/**
 * Alcance do braço, em tiles de distância de Chebyshev.
 *
 * Espelha o `REACH` de `region/systems/tools.ts`, que é a autoridade — o servidor recusa o
 * que passar disto, aqui ou não. O cliente o conhece para **não oferecer** o que seria
 * recusado: menu que propõe "abrir" numa porta a dez tiles, e arraste que atravessa a tela
 * para ouvir "está longe demais".
 */
const REACH_TILES = 1;

/**
 * Um arraste que nasceu no cenário.
 *
 * Nasce **suspenso** no `pointerdown` — `active: false` — e só vira arraste quando o ponteiro
 * anda. Até lá é um clique em potencial, e é essa suspensão que permite ao mesmo botão
 * esquerdo pegar um item com um toque e arrastá-lo com um gesto: a diferença entre os dois só
 * existe depois que o dedo se mexe (ou não).
 */
interface WorldDrag {
  /** Tile de origem, e o andar em que ele estava quando o gesto começou. */
  x: number;
  y: number;
  z: number;
  /** Índice da pilha dentro do tile. Ver `GroundTopStack`. */
  slot: number;
  typeId: number;
  count: number;
  /** Onde o ponteiro desceu, em coordenadas de jogo — a régua do `DRAG_SLOP_PX`. */
  fromX: number;
  fromY: number;
  active: boolean;
}

export interface GameSceneData {
  sessionToken: string;
  vocation: Vocation;
  /** Credenciais da conta, guardadas para pedir uma sessão nova após uma queda. */
  accountToken: string;
  characterId: number;
  /** Porta da região, no modo processo por região (PRD §3.4). */
  gamePort?: number;
}

export class GameScene extends Phaser.Scene {
  private connection!: GameConnection;
  private controller!: InputController;
  private touch!: TouchControls;

  private hud!: Hud;
  private chat!: ChatPanel;
  private inventory!: InventoryWindow;
  private equipment!: EquipmentPanel;
  private hotkeys!: HotkeyBar;
  private effects!: EffectLayer;
  private ground!: GroundItemLayer;
  private banner!: ConnectionBanner;
  private commerce!: CommercePanel;
  private social!: SocialPanel;
  private minimap!: Minimap;
  private contextMenu!: ContextMenu;
  private bestiary!: BestiaryPanel;
  private automation!: AutomationPanel;
  private marks!: PointerMarks;
  private ghost!: DragGhost;
  private readonly audio = new AudioEngine();

  private readonly predictor = new Predictor();

  /** Caminhada em curso pedida por clique no chão. Vazia enquanto ninguém clicou. */
  private readonly walk = new WalkPath();

  /** Arraste começado no cenário, suspenso ou em curso. Ver `WorldDrag`. */
  private drag: WorldDrag | null = null;

  /** Cancela o cronômetro do toque longo. Preenchido por `setupLongPress`. */
  private cancelLongPress: () => void = () => {};

  /** Solta os ouvintes de DOM da cena de uma vez só, no encerramento. */
  private readonly listeners = new AbortController();

  private terrain: Terrain | undefined;
  private region: WelcomePacket | undefined;
  private localEntityId = 0;
  private vocation = 0;

  private readonly sprites = new Map<number, CreatureSprite>();
  /** Último estado conhecido de cada entidade, para o HUD e a checagem de alvo. */
  private readonly states = new Map<number, EntityState>();
  private readonly names = new Map<number, string>();
  /** Ids com quem se conversa em vez de lutar. Vem do `EntityInfo`, uma vez por entidade. */
  private readonly friendly = new Set<number>();

  private targetId: number | null = null;

  /**
   * A postura **confirmada pelo servidor** (PRD §5), e o alcance da arma equipada.
   *
   * Os dois chegam no `Stats` e nenhum dos dois é deduzido aqui: é o servidor que aplica os
   * multiplicadores e é ele que sabe qual arma está na mão. O cliente os usa para uma coisa
   * só — decidir se persegue o alvo e até onde —, e uma cópia local que se adiantasse ao
   * pacote faria o personagem parar de andar a três tiles do lobo por achar que ainda estava
   * de arco na mão.
   */
  private stance: CombatStance = DEFAULT_STANCE;
  private attackRange = MELEE_RANGE;

  private serverTick = 0;
  private pingAccumulatorMs = 0;
  private corrections = 0;
  private connected = false;

  /** Guardado da tela de login, para pedir uma sessão nova ao reconectar. */
  private accountToken = '';
  private characterId = 0;
  /** Porta da região atual, no modo processo por região. Ver `socketUrl`. */
  private gamePort: number | undefined;
  private reconnecting = false;

  constructor() {
    super('game');
  }

  init(data: GameSceneData): void {
    this.vocation = data.vocation;
    this.accountToken = data.accountToken;
    this.characterId = data.characterId;
    this.gamePort = data.gamePort;
  }

  /**
   * Toda a arte é gerada aqui, antes do primeiro quadro.
   *
   * Gerar sob demanda pouparia memória e traria engasgo no primeiro personagem de cada
   * tipo que entrasse em cena — exatamente quando o jogador está prestando atenção.
   */
  create(data: GameSceneData): void {
    buildTileset(this);
    buildCharacterAtlases(this);
    buildCreatureAtlases(this);
    buildItemIcons(this);
    buildRarityHalos(this);
    buildEffectTextures(this);

    this.effects = new EffectLayer(this);
    this.ground = new GroundItemLayer(this);
    this.marks = new PointerMarks(this);
    // O mesmo ghost da interface, e não um da cena: os dois arrastes — o do cenário e o dos
    // slots — são o mesmo ícone atrás do mesmo cursor, e nunca acontecem juntos.
    this.ghost = dragGhost();
    this.controller = new InputController(this);

    this.setupInterface();
    this.setupTargeting();
    this.setupWorldDrop();

    // O ghost mora no `body` e os ouvintes de arraste, na `window`: os dois sobrevivem à
    // cena, e sem isto uma cena recriada empilharia um ghost órfão e um segundo conjunto de
    // ouvintes disputando o mesmo gesto.
    //
    // `hide` e não `destroy`: o ghost é **um só** na página e a interface fora da cena
    // continua usando o mesmo nó (ver `dragGhost`). Destruí-lo aqui apagaria o ícone do
    // arraste da mochila logo depois de atravessar um portal. O que a cena precisa desfazer é
    // o gesto dela que ficou pela metade, e isso é escondê-lo.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.listeners.abort();
      this.ghost.hide();
      this.marks.destroy();
      clearDropHighlight();
    });

    this.setupZoomControl();
    this.applyZoom();
    // O `viewport` muda de tamanho com a janela e com a barra lateral do celular; sem
    // reagir ao redimensionamento, o enquadramento fica preso ao tamanho da entrada.
    this.scale.on(Phaser.Scale.Events.RESIZE, () => this.applyZoom());

    this.openConnection(data.sessionToken, this.gamePort);
  }

  /**
   * Abre o socket do jogo. Chamada na entrada e a cada reconexão bem-sucedida — o mesmo
   * caminho nos dois casos, porque o servidor não distingue um do outro: em ambos chega um
   * token de sessão novo no primeiro frame e um Welcome de volta.
   */
  private openConnection(sessionToken: string, port?: number, host?: string): void {
    const url = this.socketUrl(port, host);

    const connection = new GameConnection(url, sessionToken, {
      onWelcome: (packet) => this.onWelcome(packet),
      onQueue: (packet) => this.banner.showQueue(packet.position, packet.total),
      onRedirect: (packet) => this.onRedirect(packet),
      onShop: (packet) => this.commerce.setShop(packet),
      onBank: (packet) => this.commerce.setBank(packet),
      onTrade: (packet) => this.commerce.setTrade(packet),
      onSocial: (packet) => this.social.setState(packet),
      onMapChunk: (packet) => this.onMapChunk(packet),
      onTileUpdate: (packet) => this.onTileUpdate(packet),
      onBestiary: (packet) => this.bestiary.setEntries(packet),
      onLootFilter: (packet) => this.automation.setLootFilter(packet),
      onAutomation: (packet) => this.automation.setState(packet),
      onSnapshot: (packet) => this.onSnapshot(packet),
      onDespawn: (packet) => this.onDespawn(packet),
      onCombat: (packet) => this.onCombat(packet),
      onStats: (packet) => this.onStats(packet),
      onEntityInfo: (packet) => this.onEntityInfo(packet),
      onServerMessage: (packet) => this.onServerMessage(packet),
      onInventory: (packet) => this.onInventory(packet),
      onGroundItems: (packet) => this.onGroundItems(packet),
      onEffect: (packet) => this.onEffect(packet),
      onChat: (packet) => this.onChat(packet),
      onRtt: () => {},
      onClose: () => {
        // Só a conexão **corrente** derruba o jogo. O `close` do socket antigo chega
        // depois de a reconexão já ter aberto o novo, e tratá-lo levantaria o aviso de
        // queda por cima de uma conexão saudável.
        if (this.connection !== connection) return;
        this.onDisconnected();
      },
    });

    this.connection = connection;
    connection.connect();
  }

  /**
   * Endereço do socket de jogo.
   *
   * `port` chega no modo processo por região (PRD §3.4) — do `/api/play` na entrada, e do
   * `Redirect` a cada travessia de portal. Sem ele vale a URL de configuração, que é o
   * modelo de gateway único: uma porta para o mundo inteiro.
   *
   * O **host** nunca é trocado por aqui: o servidor manda um `host` vazio quando o destino
   * está na mesma máquina, que é o caso normal e o único em que trocar de porta basta.
   * Aceitar host arbitrário do servidor seria dar a ele o poder de mandar o cliente
   * autenticar-se em qualquer lugar.
   */
  private socketUrl(port?: number, host?: string): string {
    const configured =
      (import.meta.env['VITE_WS_URL'] as string | undefined) ?? 'ws://localhost:8080';

    if (port === undefined || port <= 0) return configured;

    const url = new URL(configured);
    if (host) url.hostname = host;
    url.port = String(port);
    return url.toString().replace(/\/$/, '');
  }

  /**
   * O servidor mandou continuar noutro processo (PRD §3.4).
   *
   * É a travessia de portal quando cada região é um processo com porta própria: o
   * personagem já foi gravado descrito do outro lado, e o que falta é o socket mudar de
   * lugar. O token vem no pacote porque o desta conexão já foi consumido no primeiro frame.
   *
   * `region` é zerado antes de reabrir para que o Welcome seguinte seja lido como chegada a
   * uma região nova — que é exatamente o que ele é.
   */
  private onRedirect(packet: RedirectPacket): void {
    this.chat.addSystem('mudando de região…');

    this.region = undefined;
    this.connection.close();
    this.openConnection(packet.token, packet.port, packet.host);
  }

  /**
   * A conexão caiu.
   *
   * `connected` volta a `false` e é `pollInput` quem respeita a bandeira: sem isso o
   * personagem continuava andando por predição num mundo que já não respondia, câmera
   * junto, e a única pista era uma linha no chat que ninguém lê no meio de uma briga.
   */
  private onDisconnected(): void {
    this.connected = false;
    this.chat.addSystem('conexão encerrada.');
    this.banner.show();
  }

  /**
   * Pede uma sessão nova e reabre o socket.
   *
   * Não é o mesmo token: ele é de uso único e já foi gasto no primeiro frame da conexão
   * anterior. O que sobrevive é o token de **conta**, e com ele o Login Server emite outra
   * sessão sem exigir a senha de novo.
   */
  private async reconnect(): Promise<void> {
    if (this.reconnecting) return;
    this.reconnecting = true;
    this.banner.setBusy('pedindo uma sessão nova…');

    try {
      const { sessionToken, gamePort } = await requestPlaySession(
        this.accountToken,
        this.characterId,
      );

      // A porta pode ter mudado desde a entrada: no modo processo por região (PRD §3.4) o
      // jogador reconecta na região **em que estava**, e não na que abriu a sessão.
      this.gamePort = gamePort;

      // O Welcome que vem a seguir precisa ser lido como uma entrada, e não como a
      // chegada a outra região: sem zerar isto, o chat anunciaria "você chegou a Vila do
      // Norte" para quem nunca saiu de lá.
      this.region = undefined;
      this.connection.close();
      this.openConnection(sessionToken, this.gamePort);
    } catch (error) {
      this.banner.setError(
        error instanceof ApiError
          ? `${error.message}. Se persistir, volte ao login.`
          : 'não foi possível reconectar. Volte ao login.',
      );
    } finally {
      this.reconnecting = false;
    }
  }

  // --- Interface -----------------------------------------------------------

  private setupInterface(): void {
    this.hud = new Hud(requireElement('hud'));

    this.chat = new ChatPanel(requireElement('chat'), {
      onSay: (channel, to, text) => this.connection.sendChat({ channel, to, text }),
    });

    // Os mesmos dois gestos nos dois painéis, e por isso o mesmo par de funções: arrastar o
    // elmo da mochila para a cabeça e tirá-lo de volta são o mesmo `MoveItem` com origem e
    // destino trocados, e quem decide se vale é o servidor.
    const itemHandlers = {
      onMove: (
        fromContainer: number,
        fromSlot: number,
        toContainer: number,
        toSlot: number,
        count: number,
      ) =>
        this.connection.sendMoveItem({
          fromContainer,
          fromSlot,
          toContainer,
          toSlot,
          count,
        }),
      onUse: (container: number, slot: number, typeId: number) =>
        this.useFromInventory(container, slot, typeId),
    };

    this.equipment = new EquipmentPanel(requireElement('equipment'), {
      ...itemHandlers,
      onStance: (stance) => this.chooseStance(stance),
    });
    this.inventory = new InventoryWindow(requireElement('inventory'), itemHandlers);

    // O ícone é a textura do Phaser convertida em data URL: o DOM não enxerga a
    // textura, e desenhar o item duas vezes — uma no canvas, outra no DOM — deixaria
    // as duas aparências livres para divergir. O ghost do arraste sai da mesma função,
    // pelo mesmo motivo: o item que acompanha o cursor é o que está no chão.
    //
    // Um resolvedor só para toda a interface (ver `ui/slot.ts`): equipamento, inventário e
    // mesa de troca desenham a mesma espada, e um cache por painel converteria a mesma
    // textura em base64 três vezes.
    setItemIconResolver((typeId) => this.itemIcon(typeId));

    this.commerce = new CommercePanel(requireElement('commerce'), {
      onBuy: (typeId, count) =>
        this.connection.sendShop({ action: ShopAction.Buy, typeId, count }),
      onSell: (typeId, count) =>
        this.connection.sendShop({ action: ShopAction.Sell, typeId, count }),
      // Sem item nem quantidade: o conserto é de tudo de uma vez, e o servidor ignora os
      // dois campos. Zerá-los aqui é o que impede um `typeId` de sobra de virar, um dia, um
      // conserto "daquela peça" que ninguém pediu (PRD §15.7).
      onRepair: () =>
        this.connection.sendShop({ action: ShopAction.Repair, typeId: 0, count: 0 }),
      onCloseShop: () =>
        this.connection.sendShop({ action: ShopAction.Close, typeId: 0, count: 0 }),

      onDeposit: (amount) =>
        this.connection.sendBank({ action: BankAction.Deposit, amount }),
      onWithdraw: (amount) =>
        this.connection.sendBank({ action: BankAction.Withdraw, amount }),
      onCloseBank: () => this.connection.sendBank({ action: BankAction.Close, amount: 0 }),

      onTradeAccept: () => this.sendTrade(TradeAction.Accept),
      onTradeCancel: () => this.sendTrade(TradeAction.Cancel),
      onTradeWithdraw: (index) => this.sendTrade(TradeAction.Withdraw, 0, 0, index),
      onTradeOffer: (container, slot) =>
        this.sendTrade(TradeAction.Offer, 0, container, slot),
    });

    this.social = new SocialPanel(requireElement('social'), {
      onAction: (action, name) => this.connection.sendSocial(action, name),
    });

    // Clicar no minimapa anda até lá, exatamente como clicar no chão: o mesmo `startWalk`,
    // e por isso as mesmas regras — tile desconhecido recusa, criatura no caminho é
    // contornada, Esc cancela. O que muda é só o alcance, e ele é o motivo do gesto existir.
    this.minimap = new Minimap(requireElement('minimap'), {
      onPick: (x, y) => this.startWalk(x, y),
    });
    this.contextMenu = new ContextMenu(requireElement('context-menu'));
    this.bestiary = new BestiaryPanel(requireElement('bestiary'));
    this.automation = new AutomationPanel(requireElement('automation'), {
      onChange: (packet) => this.connection.sendAutomation(packet),
      onLootFilterChange: (packet) => this.connection.sendLootFilter(packet),
    });
    const audioControls = mountAudioControls(requireElement('audio'), this.audio);

    this.input.keyboard?.on('keydown-M', () => {
      if (this.chat.isTyping) return;
      this.toggleMinimap();
    });

    this.input.keyboard?.on('keydown-B', () => {
      if (this.chat.isTyping) return;
      this.toggleBestiary();
    });

    this.input.keyboard?.on('keydown-O', () => {
      if (this.chat.isTyping) return;
      this.toggleSocial();
    });

    this.input.keyboard?.on('keydown-L', () => {
      if (this.chat.isTyping) return;
      this.toggleAutomation();
    });

    this.hotkeys = new HotkeyBar(requireElement('hotkeys'), {
      onCast: (spell) => {
        const target = spell.target === SpellTarget.Creature ? (this.targetId ?? 0) : 0;
        this.connection.sendCast(spell.id, target, this.predictor.x, this.predictor.y);
        this.sprites.get(this.localEntityId)?.playCast();
      },
    });
    this.hotkeys.setVocation(this.vocation as Vocation);

    // O direcional de toque alimenta o **mesmo** `DirectionState` do teclado. Antes ele
    // era lido como estado instantâneo a cada quadro, e o efeito era o buffer de input
    // existir só no caminho do teclado: no celular, oito toques de 60 ms davam zero passos
    // (PRD §11.2, item 5). É justamente onde todo comando é um toque curto que o buffer
    // mais importa.
    this.touch = new TouchControls(requireElement('touch'), {
      onPress: (direction) => this.controller.press(direction),
      onRelease: (direction) => this.controller.release(direction),
      // Os mesmos métodos das teclas, e não um caminho paralelo: o bestiário que pede a ficha
      // ao abrir, a caçada que não pede nada, o som de interface — tudo isso é decisão de
      // painel, não de dispositivo. Duas cópias seriam duas chances de o toque abrir o
      // bestiário sem pedir a ficha e mostrar uma lista vazia a quem já caçou.
      //
      // A ordem é a da frequência: mochila e mapa se abrem no meio da caçada, automações e
      // bestiário entre uma e outra, amigos e volume uma vez por sessão. `Parar` fica por
      // último de propósito — é a saída de emergência, e uma saída de emergência no meio da
      // lista é a que se aperta sem querer enquanto se procura outra coisa.
      actions: [
        { label: 'Mochila', onPick: () => this.inventory.toggle() },
        { label: 'Mapa', onPick: () => this.toggleMinimap() },
        { label: 'Automações', onPick: () => this.toggleAutomation() },
        { label: 'Bestiário', onPick: () => this.toggleBestiary() },
        { label: 'Amigos', onPick: () => this.toggleSocial() },
        { label: 'Volume', onPick: () => audioControls.toggle() },
        { label: 'Parar', onPick: () => this.cancelOrders() },
      ],
    });

    this.banner = new ConnectionBanner(requireElement('connection'), {
      onReconnect: () => void this.reconnect(),
    });

    window.addEventListener('keydown', (event) => {
      if (event.key.toLowerCase() !== 'i' || this.chat.isTyping) return;
      this.inventory.toggle();
    });
  }

  // --- Os menus, num lugar só ------------------------------------------------
  //
  // Cada um destes era o corpo de um `keydown-X`, e passou a método quando o menu de toque
  // (ver `input/touch.ts`) virou o segundo jeito de chamar os mesmos painéis. O que estes
  // métodos guardam não é o `toggle` — esse já mora no painel —, é o que **acompanha** o
  // abrir: o pedido ao servidor, o som, e a diferença entre um painel e outro. Foi para não
  // reescrever essa parte no caminho do dedo que eles existem.
  //
  // A guarda de `chat.isTyping` fica **fora**, com quem chama: no teclado, `M` no meio de uma
  // frase é a letra M; no menu de toque não há frase nenhuma para atrapalhar, e um botão que
  // recusasse o toque porque o campo de chat está focado seria um botão quebrado.

  private toggleMinimap(): void {
    this.minimap.toggle();
    this.audio.play(Sfx.Ui);
  }

  /**
   * O bestiário é consulta, como o social: nasce fechado, e o pedido só sai quando ele abre.
   * Pedir a ficha inteira a cada tick para uma tela que passa a sessão escondida seria trinta
   * pacotes por minuto para ninguém ler.
   */
  private toggleBestiary(): void {
    if (this.bestiary.toggle()) this.connection.sendBestiaryRequest();
    this.audio.play(Sfx.Ui);
  }

  /**
   * O painel social não é algo que se acompanha durante a briga: é consulta, e por isso nasce
   * fechado. `O` de "online", que é a pergunta que ele responde.
   */
  private toggleSocial(): void {
    this.social.toggle();
  }

  /**
   * As três automações — auto-loot, auto-comida e auto-caçada (PRD §6).
   *
   * Não pede nada ao servidor ao abrir, ao contrário do bestiário: tudo chegou no `join`, e a
   * rota chega de novo sozinha a cada ponto gravado.
   */
  private toggleAutomation(): void {
    this.automation.toggle();
    this.audio.play(Sfx.Ui);
  }

  /**
   * "Cancele o que eu pedi": larga o alvo e para a caminhada. É o que a tecla Esc faz.
   *
   * No toque isto não é conveniência, é a única forma de desistir. Um alvo escolhido faz o
   * personagem perseguir e bater sozinho, e uma caminhada clicada no minimapa atravessa a
   * região inteira — sem tecla, o jeito de parar as duas coisas era andar na direção
   * contrária com o direcional, brigando com a ordem que o próprio jogo estava cumprindo.
   */
  private cancelOrders(): void {
    this.selectTarget(null);
    this.walk.clear();
  }

  /**
   * Ajusta o zoom da câmera ao tamanho da área de jogo.
   *
   * O fator anda de meio em meio, e não de um em um. Inteiro seria o ideal para pixel
   * art — cada pixel da arte vira um quadrado exato —, mas entre 1× e 2× não há nada, e
   * o salto é grande demais: 1× larga o personagem no meio de quarenta tiles e 2× fecha
   * o campo a dezoito. A 1,5× umas colunas de pixel ficam com um pixel de tela e outras
   * com dois, o que se nota num detalhe de um pixel e não se nota no resto — preço baixo
   * por um enquadramento que serve.
   *
   * O menor dos dois eixos manda. Calcular só pela largura funcionava em 16:9 e falhava
   * exatamente onde há mais tela: numa ultrawide em tela cheia a horizontal pedia 4× ou
   * mais, o zoom batia no teto e o campo **vertical** — que continua curto — fechava
   * junto. Quem limita o enquadramento é sempre o lado mais apertado.
   */
  private applyZoom(): void {
    if (this.zoomPreference !== null) return;

    const { width, height } = this.scale.gameSize;
    if (width <= 0 || height <= 0) return;

    const wanted = Math.min(
      width / (TILES_ACROSS * TILE_SIZE),
      height / (TILES_DOWN * TILE_SIZE),
    );

    this.setZoom(Math.round(wanted / ZOOM_STEP) * ZOOM_STEP);
  }

  private setZoom(zoom: number): number {
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom));
    this.cameras.main.setZoom(clamped);
    return clamped;
  }

  /**
   * Roda do mouse aproxima e afasta, e a escolha fica guardada.
   *
   * Enquadramento é preferência, e monitor é coisa pessoal: nenhuma fórmula acerta para
   * todo mundo. A partir do primeiro giro da roda o cálculo automático sai de cena — nem
   * redimensionar a janela o traz de volta —, e a escolha sobrevive ao recarregamento.
   */
  private setupZoomControl(): void {
    const stored = Number.parseFloat(readSetting(ZOOM_STORAGE_KEY) ?? '');
    if (Number.isFinite(stored)) {
      this.zoomPreference = this.setZoom(stored);
    }

    this.input.on(
      'wheel',
      (_pointer: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number) => {
        if (dy === 0) return;

        const next = this.setZoom(this.cameras.main.zoom + (dy < 0 ? ZOOM_STEP : -ZOOM_STEP));
        this.zoomPreference = next;
        writeSetting(ZOOM_STORAGE_KEY, String(next));
      },
    );
  }

  /** `null` enquanto o jogador não mexeu: é o que autoriza o cálculo automático. */
  private zoomPreference: number | null = null;

  // --- Rede ----------------------------------------------------------------

  /**
   * Entrada numa região — a primeira, ou qualquer outra alcançada por portal (PRD §6).
   *
   * Não é um evento de conexão, é um evento de **mundo**: o Welcome chega de novo a cada
   * travessia, com outro mapa e outros ids de entidade. Tudo que se sabia sobre o mundo
   * anterior tem que ir embora aqui, porque os ids se repetem entre regiões e um sprite
   * sobrevivente seria desenhado como se fosse outra criatura.
   */
  private onWelcome(packet: WelcomePacket): void {
    const arriving = this.region !== undefined;

    this.clearWorld();

    this.region = packet;
    this.localEntityId = packet.entityId;
    this.serverTick = packet.tick;
    this.predictor.stepDurationMs = packet.stepMs;
    this.connected = true;

    // A janela de desenho sai do enquadramento **mais aberto** que o jogador pode pedir, e
    // não do atual: afastar o zoom depois não pode revelar uma faixa que a janela não cobre,
    // e recriar as camadas a cada giro da roda seria um engasgo por giro.
    const camera = this.cameras.main;
    this.terrain = new Terrain(
      this,
      packet.width,
      packet.height,
      packet.floors,
      Math.ceil(camera.width / ZOOM_MIN / TILE_SIZE),
      Math.ceil(camera.height / ZOOM_MIN / TILE_SIZE),
    );
    this.hud.setPlace(packet.regionName, packet.groundFloor, packet.groundFloor);
    // A tela de automações precisa saber onde o personagem está para dizer se a rota gravada
    // vale aqui. Uma rota é de um lugar (PRD §6), e atravessar um portal a deixa parada — sem
    // esta linha, o painel mostraria a caçada ligada e o personagem imóvel, sem explicação.
    this.automation.setRegion(packet.regionId, packet.regionName);
    this.banner.hide();

    // A postura escolhida volta a valer aqui, e não na criação da cena: o servidor põe todo
    // mundo em `Balanced` na entrada, e a região do outro lado de um portal é outro processo
    // que nunca ouviu falar deste personagem (PRD §3.4). Sem este reenvio, atravessar uma
    // porta devolveria o Knight que luta em defesa ao equilíbrio, em silêncio, no meio da
    // caçada — e o botão continuaria aceso mentindo até o `Stats` seguinte.
    this.connection.sendStance(this.storedStance());

    this.cameras.main.setBounds(0, 0, packet.width * TILE_SIZE, packet.height * TILE_SIZE);

    // "à **região** X", e não "à X": a crase se apoia em "região", que é invariavelmente
    // feminino, e o nome do mapa vira aposto. O nome vem da propriedade `name` do mapa e
    // ninguém garante o gênero dele — "Caverna do Eco" é feminino, mas as próprias fixtures
    // deste repositório já usam "Destino" (tests/e2e/regions.test.ts). Qualquer artigo
    // escolhido por adivinhação erraria dentro do próprio repo.
    //
    // Minúscula de propósito: linha de sistema do cliente é minúscula (ver as vizinhas
    // aqui e em `ChatPanel`), fala do servidor é maiúscula. São dois registros.
    this.chat.addSystem(
      arriving
        ? `você chegou à região ${packet.regionName}.`
        : 'conectado. use as setas ou WASD para andar.',
    );
  }

  /** Esvazia tudo que pertencia à região anterior. */
  private clearWorld(): void {
    // Soltar a câmera antes de destruir os sprites: ela segue o alvo do personagem
    // local, e continuar seguindo um objeto destruído até o primeiro snapshot da região
    // nova travaria o enquadramento onde ele estava.
    this.cameras.main.stopFollow();

    // Loja, banco e troca não atravessam a fronteira da região: o NPC ficou do outro lado
    // do portal, e uma janela aberta apontando para ele prometeria uma ação já recusada.
    this.commerce.clear();
    this.minimap.reset();

    for (const sprite of this.sprites.values()) sprite.destroy();
    this.sprites.clear();
    this.states.clear();
    this.friendly.clear();
    this.names.clear();

    this.targetId = null;
    this.ground.update({ tiles: [] });

    // Um caminho é uma lista de passos sobre **aquele** mapa, e um arraste é um endereço de
    // tile nele. Os dois viram lixo na fronteira da região, e um passo sobrevivente andaria
    // o personagem para um lado qualquer assim que o Welcome novo chegasse.
    this.walk.clear();
    this.drag = null;
    this.ghost.hide();
    this.marks.clearHighlight();

    this.terrain?.destroy();
    this.terrain = undefined;
  }

  private onMapChunk(packet: MapChunkPacket): void {
    this.terrain?.patch(packet);

    // Chunk novo é terreno que o minimapa não tinha: sem o redesenho forçado, o pedaço
    // recém-recebido só apareceria no próximo passo do jogador.
    if (this.terrain) {
      this.minimap.draw(
        this.terrain,
        this.predictor.x,
        this.predictor.y,
        this.terrain.floor,
        [...this.states.values()],
        this.localEntityId,
        true,
      );
    }
  }

  /**
   * Um tile mudou (PRD §15.2): porta que abriu, alavanca que virou, buraco que foi cavado.
   *
   * O redesenho do minimapa é **forçado** pela mesma razão do chunk novo: o terreno mudou
   * sem ninguém ter andado, e sem o `force` o pedaço corrigido só apareceria no próximo
   * passo do jogador. Sem estas duas linhas o pacote sai do servidor e não faz nada na
   * tela — que é uma classe de defeito pior que a de não mandar o pacote, porque o
   * servidor fica com a razão e ninguém vê.
   */
  private onTileUpdate(packet: TileUpdatePacket): void {
    const terrain = this.terrain;
    if (!terrain) return;
    if (!terrain.patchTile(packet.x, packet.y, packet.z, packet.tile)) return;
    if (packet.z !== terrain.floor) return;

    this.minimap.draw(
      terrain,
      this.predictor.x,
      this.predictor.y,
      terrain.floor,
      [...this.states.values()],
      this.localEntityId,
      true,
    );
  }

  private onSnapshot(packet: SnapshotPacket): void {
    this.serverTick = packet.tick;

    // O pacote traz só o que mudou (PRD §6). `applySnapshot` funde a diferença sobre o que
    // o cliente já sabia e devolve o estado completo **das entidades mencionadas** — quem
    // não veio não mudou, e continua em `states` como estava.
    const updated = applySnapshot(this.states, packet);

    for (const entity of updated) {
      // Escada: o servidor moveu o personagem de andar, e o cenário inteiro muda com ele.
      // Vem daqui e não de um pacote próprio porque o andar já viaja no Snapshot, e um
      // segundo pacote só para anunciá-lo poderia chegar fora de ordem com a posição.
      if (entity.id === this.localEntityId && this.terrain && entity.z !== this.terrain.floor) {
        this.changeFloor(entity);
      }
    }

    for (const entity of updated) {
      const sprite = this.ensureSprite(entity);

      sprite.setHealth(entity.health, entity.maxHealth);
      sprite.setOutfit(entity.vocation, entity.outfitTier);
      sprite.setPvpMark(entity.pvpMark);

      if (entity.id === this.localEntityId) {
        // A velocidade pode mudar em jogo (piso, buff — PRD §5). Cada passo confirmado
        // traz a duração real, então o cliente reacompanha sozinho em vez de ficar preso
        // no valor que veio no Welcome.
        if (entity.stepMs > 0) {
          this.predictor.stepDurationMs = entity.stepMs;
          sprite.setStepDuration(entity.stepMs);
        }

        // Posição e direção do personagem local saem da reconciliação, que roda uma vez
        // por Snapshot logo abaixo — venha ele no delta ou não.
        continue;
      }

      sprite.view.moveTo(entity.x, entity.y, entity.stepMs);
      sprite.setDirection(entity.direction);
      if (entity.stepMs > 0) sprite.setStepDuration(entity.stepMs);
    }

    // Fora do laço, e por isso depois dele: `ensureSprite` é quem cria o sprite local no
    // primeiro Snapshot, e é ele que chama `predictor.reset`.
    this.reconcileLocal(packet.ackSeq);
  }

  /**
   * Confronta a predição com o estado autoritativo em **todo** Snapshot, e não só quando o
   * personagem local vem no delta.
   *
   * O passo recusado é justamente o que não muda nada no servidor: ele não anda e, se já
   * estava virado para aquele lado, nem a direção muda. Máscara zero em `World.deltaFor`,
   * entidade omitida do pacote — e enquanto a reconciliação morava dentro do laço de
   * `updated`, ela não rodava exatamente no caso que precisa dela. O cliente andava por
   * cima de criaturas sem nada que o puxasse de volta (7 tiles de divergência medidos em
   * jogo, com o chat respondendo "está longe demais" a um NPC colado no personagem), até
   * acertar um passo que o servidor aceitasse.
   *
   * A entrada é `this.states` e não o pacote: `applySnapshot` funde o delta sobre o
   * acumulado, e quem não veio continua lá com o último valor autoritativo — que, para um
   * passo recusado, é a posição certa. O `ackSeq` viaja no cabeçalho de todo Snapshot, e o
   * servidor confirma o input mesmo recusando o passo (`systems/movement.ts`), então
   * `reconcile` sempre encontra em `pending` o passo que previmos errado.
   *
   * ## Por que o passo que **não** é nosso desliza
   *
   * O personagem local é o único da cena cujo desenho não sai do Snapshot: ele sai da
   * predição, e a predição só sabe dos passos que o teclado pediu. Enquanto isto tratava
   * toda divergência como mispredição, tudo que movia o personagem sem input do cliente
   * chegava aqui como salto de 32 px — e a auto-caçada percorre a rota inteira assim, um
   * salto por passo, sem nunca passar por `applyLocalStep`.
   *
   * O que se via era exatamente o defeito relatado: o boneco pisca de tile em tile, parado
   * na pose de repouso (a caminhada é ligada por `EntityView.isMoving`, que um salto deixa
   * falso), com a câmera — que segue por interpolação — correndo atrás em solavancos. Um
   * cavebot que anda direito parecia travado.
   *
   * A régua é a do próprio pacote: `stepMs` não nulo com um tile cardeal de distância é um
   * **passo**, e passo se anima com a duração que veio dentro dele. Qualquer outro salto
   * (escada, teleporte, empurrão para longe) continua sendo salto, porque animá-lo desenharia
   * um caminho que ninguém percorreu.
   */
  private reconcileLocal(ackSeq: number): void {
    const state = this.states.get(this.localEntityId);
    const sprite = this.sprites.get(this.localEntityId);
    if (!state || !sprite) return;

    // Andar diferente do desenhado: quem reposiciona é `changeFloor`, que já reiniciou a
    // predição neste mesmo tick. Corrigir aqui seria puxar o personagem contra um mapa que
    // não é mais o dele.
    if (this.terrain && state.z !== this.terrain.floor) return;

    const fromX = this.predictor.x;
    const fromY = this.predictor.y;

    switch (this.predictor.reconcile(state.x, state.y, ackSeq)) {
      case Correction.Mispredicted:
        this.corrections += 1;
        // Sem duração: a correção é um salto. Animá-la mostraria o personagem deslizando de
        // volta por um caminho que ele nunca percorreu.
        sprite.view.moveTo(this.predictor.x, this.predictor.y, 0);
        break;

      case Correction.External: {
        const walked =
          state.stepMs > 0 &&
          Math.abs(state.x - fromX) + Math.abs(state.y - fromY) === 1;

        // Só o salto conta como correção. Um passo que o servidor deu por nós não é uma
        // predição errada — é movimento que o cliente nunca tentou prever —, e contá-lo
        // faria o diagnóstico marcar milhares de "correções" numa caçada que está certa,
        // escondendo a mispredição de verdade no meio delas.
        if (!walked) this.corrections += 1;

        sprite.view.moveTo(this.predictor.x, this.predictor.y, walked ? state.stepMs : 0);

        // A direção autoritativa junto, e só no passo: sem ela o personagem percorre a rota
        // de costas, olhando para o lado da última tecla que o jogador apertou. Vai para o
        // `Predictor` e não direto para o sprite porque é dele que sai o `setDirection`
        // abaixo — e porque é contra ele que `pollInput` compara ao decidir se o próximo
        // Move é um passo ou uma virada.
        if (walked) this.predictor.direction = state.direction;
        break;
      }
    }

    sprite.setDirection(this.predictor.direction);
  }

  /**
   * Troca o andar visível e recoloca o personagem sem animar.
   *
   * A predição é reiniciada em vez de reconciliada: os passos em voo foram calculados
   * sobre o andar de baixo, e aplicá-los aqui moveria o personagem por um mapa que já
   * não é o dele. Reiniciar custa, no pior caso, um input perdido no instante da subida.
   */
  private changeFloor(entity: EntityState): void {
    // O caminho morre com o andar, pela mesma razão que a predição é reiniciada: os passos
    // que faltam foram traçados sobre o mapa de baixo, e continuar a executá-los levaria o
    // personagem por um corredor que já não é o dele.
    this.walk.clear();

    // A janela primeiro: escada e teleporte podem pôr o personagem longe de onde ele
    // estava, e rolar depois do `setFloor` custaria dois redesenhos em vez de um.
    this.terrain?.follow(entity.x, entity.y);
    this.terrain?.setFloor(entity.z);
    this.hud.setPlace(this.region?.regionName ?? '', entity.z, this.region?.groundFloor ?? 0);

    this.predictor.reset(entity.x, entity.y);
    this.sprites.get(this.localEntityId)?.view.moveTo(entity.x, entity.y, 0);
  }

  private onDespawn(packet: DespawnPacket): void {
    for (const id of packet.ids) {
      if (id === this.localEntityId) continue;

      const sprite = this.sprites.get(id);
      if (sprite) {
        // Morte e saída de vista chegam pelo mesmo pacote. Animar a morte de quem só
        // andou para fora da tela seria mentira; animar quem morreu diante do jogador é
        // o mínimo. A vida zerada no último snapshot é o que distingue os dois.
        const died = (this.states.get(id)?.health ?? 1) <= 0;
        if (died) sprite.playDeath();

        this.time.delayedCall(died ? 300 : 0, () => sprite.destroy());
      }

      this.sprites.delete(id);
      this.states.delete(id);
      this.names.delete(id);

      if (this.targetId === id) this.selectTarget(null);
    }
  }

  private onCombat(packet: CombatPacket): void {
    for (const event of packet.events) {
      this.sprites.get(event.attackerId)?.playAttack();

      // Antes da busca do sprite, e de propósito: o golpe que eu dei em algo que acabou de
      // sair da Área de Interesse não tem número flutuante para desenhar, mas continua sendo
      // dano que eu causei — e some do registro justamente no caso em que não deu para ver.
      this.logHit(event);

      const sprite = this.sprites.get(event.targetId);
      if (!sprite) continue;

      const x = sprite.view.renderX;
      const y = sprite.view.renderY - TILE_SIZE / 2;

      // O som acompanha o número flutuante e é limitado por janela (ver `AudioEngine`):
      // uma onda de fogo que atinge oito alvos produz um estalo, não oito.
      this.audio.play(event.killed ? Sfx.Death : event.missed ? Sfx.Miss : Sfx.Hit);

      if (event.missed) {
        spawnFloatingText(this, x, y, 'errou', { color: COLOR_MISS, fontSize: '11px' });
        continue;
      }

      // Dano sofrido em vermelho, dano causado em branco: com várias criaturas batendo
      // ao mesmo tempo, a cor é o que diz de relance se a briga vai bem.
      const isOnMe = event.targetId === this.localEntityId;
      spawnFloatingText(this, x, y, `-${event.damage}`, {
        color: isOnMe ? COLOR_DAMAGE_TAKEN : COLOR_DAMAGE_DEALT,
        fontSize: isOnMe ? '16px' : '14px',
      });
    }
  }

  /**
   * O golpe no registro do chat, quando ele envolve o personagem local.
   *
   * ## Só o que passa por mim
   *
   * Dois lobos brigando com um terceiro jogador do outro lado da tela produzem golpes que
   * chegam neste cliente — eles estão na Área de Interesse —, e escrevê-los aqui encheria o
   * painel com uma briga que não é minha. O número flutuante já conta essa história para
   * quem estiver olhando; o registro é sobre a **minha** luta.
   *
   * ## E só o dano
   *
   * O erro fica de fora. Com dois ataques por segundo em cada direção, anotar as tentativas
   * dobraria o volume de linhas para dizer "não aconteceu nada" — e o que se procura ao
   * rolar o histórico é quanto se levou, não quantas vezes o lobo passou perto. Quem quer
   * ver o erro tem o "errou" flutuante, que é onde ele importa: na hora.
   *
   * O dano de veneno entra por aqui também, porque o servidor o manda pela mesma lista
   * (ver `updateConditions`) — com o id de quem envenenou como autor, que é a resposta certa
   * para "quem está me matando".
   */
  private logHit(event: CombatEvent): void {
    if (event.missed || event.damage === 0) return;

    const taken = event.targetId === this.localEntityId;
    const dealt = event.attackerId === this.localEntityId;
    if (!taken && !dealt) return;

    // Bater em si mesmo é possível — uma área do próprio feitiço alcança quem a lançou —, e
    // nesse caso a linha é a do dano sofrido: é o lado que muda a decisão de quem lê.
    const other = taken ? event.attackerId : event.targetId;
    const text = taken
      ? `${this.displayName(other)} causou ${event.damage} de dano em você`
      : `Você causou ${event.damage} de dano em ${this.displayName(other)}`;

    // A morte do próprio jogador já vem anunciada pelo servidor (ver `onPlayerDeath`), mas o
    // aviso dele não diz qual golpe fechou a conta. O sufixo aqui liga uma coisa à outra.
    this.chat.addHit(taken, event.killed ? `${text} — golpe fatal.` : `${text}.`);
  }

  /**
   * Como chamar uma entidade numa linha de texto.
   *
   * O nome pode faltar por dois caminhos: o `EntityInfo` ainda não chegou, ou o autor está
   * fora da Área de Interesse — o veneno de uma aranha que ficou para trás continua cobrando
   * pulsos. Cair para a categoria é melhor que cair para o id: "criatura causou 3 de dano em
   * você" é uma frase; "4127 causou 3 de dano em você" é um vazamento de detalhe interno.
   */
  private displayName(id: number): string {
    if (id === this.localEntityId) return 'você';

    const known = this.names.get(id);
    if (known !== undefined && known.length > 0) return known;

    const kind = this.states.get(id)?.kind;
    if (kind === EntityKind.Npc) return 'criatura';
    if (kind === EntityKind.Player) return 'jogador';
    return 'algo';
  }

  private onStats(packet: StatsPacket): void {
    this.hud.setStats(packet);
    this.hotkeys.setStatus(packet.level, packet.mana);

    // A postura e o alcance vêm de carona no pacote de estado privado, que já sai só quando
    // algo muda. O botão aceso é o que chegou daqui — ver `EquipmentPanel`.
    this.stance = packet.stance;
    this.attackRange = packet.attackRange;
    this.equipment.setStance(packet.stance);
  }

  /**
   * O jogador escolheu uma postura no painel de equipamento (PRD §5).
   *
   * Guarda a preferência **antes** de mandar o pacote, e de propósito: o que fica no
   * navegador é o que a pessoa pediu, e não o que o servidor confirmou. Uma escolha feita
   * durante uma queda de conexão continua valendo no próximo login — que é o comportamento
   * que ela espera de um botão que ficou aceso na tela dela.
   *
   * O botão só acende quando o `Stats` volta. É a mesma disciplina do inventário, e cobra
   * meia viagem de rede num gesto que ninguém faz sob pressão de milissegundos.
   */
  private chooseStance(stance: CombatStance): void {
    writeSetting(STANCE_STORAGE_KEY, String(stance));
    this.connection.sendStance(stance);
  }

  /**
   * A postura guardada no navegador, ou a padrão se não houver nenhuma válida.
   *
   * A ausência é conferida **antes** da conversão, e não depois: `Number(null)` e `Number('')`
   * dão zero, e zero é `CombatStance.Offensive`. Sem esta linha, todo cliente que nunca tinha
   * escolhido nada entrava em postura ofensiva — com metade da defesa e perseguindo o alvo —
   * por causa de uma coerção, e o botão aceso na tela dizia exatamente isso sem que ninguém
   * tivesse clicado nele. Medido no primeiro quadro do teste de navegador.
   */
  private storedStance(): CombatStance {
    const raw = readSetting(STANCE_STORAGE_KEY);
    if (raw === null || raw === '') return DEFAULT_STANCE;

    const parsed = Number(raw);
    return isCombatStance(parsed) ? parsed : DEFAULT_STANCE;
  }

  private onEntityInfo(packet: EntityInfoPacket): void {
    for (const entity of packet.entities) {
      this.names.set(entity.id, entity.name);
      if (entity.friendly) this.friendly.add(entity.id);
      else this.friendly.delete(entity.id);

      this.sprites.get(entity.id)?.setName(entity.name);
    }
  }

  private onServerMessage(packet: ServerMessagePacket): void {
    this.chat.addServerMessage(packet.kind, packet.text);

    if (packet.kind === MessageKind.Advance) this.audio.play(Sfx.Advance, 200);
    else if (packet.kind === MessageKind.Loss) this.audio.play(Sfx.Loss, 200);

    // Perda e ganho também aparecem sobre o personagem: quem está olhando para a briga
    // não está lendo o painel de mensagens.
    if (packet.kind !== MessageKind.Info) {
      const sprite = this.sprites.get(this.localEntityId);
      if (!sprite) return;

      spawnFloatingText(
        this,
        sprite.view.renderX,
        sprite.view.renderY - TILE_SIZE,
        packet.text.slice(0, 40),
        {
          color: packet.kind === MessageKind.Loss ? '#ff9b6b' : '#9be59b',
          fontSize: '11px',
        },
      );
    }
  }

  private onInventory(packet: InventoryPacket): void {
    // O boneco e a mochila são dois painéis e um pacote só: vestir uma peça a tira de um e a
    // põe no outro no mesmo instante, e dois pacotes abririam a janela em que um chegou e o
    // outro não — a armadura desenhada nos dois lugares, ou em nenhum.
    this.equipment.update(packet);
    this.inventory.update(packet);

    // O balcão recebe o **mesmo** pacote, no mesmo lugar: ele lista o que o jogador carrega
    // e o NPC compra fora da vitrine (PRD §15.7). Um segundo caminho de distribuição — a
    // loja pedindo o inventário ao abrir, por exemplo — daria duas verdades sobre a mochila,
    // e a da janela seria a velha: o item vendido continuaria à venda até o próximo movimento.
    this.commerce.setInventory(packet);

    // Guarda o contêiner equipado como destino padrão do que se pega do chão. Sem isso,
    // o clique de recolher teria que perguntar ao jogador para onde vai o item — o que
    // ninguém quer responder no meio de uma caçada.
    const backpack = packet.entries.find(
      (entry) =>
        entry.parentId === CONTAINER_EQUIPMENT &&
        entry.slot === ItemSlot.Backpack,
    );
    this.lastBackpackId = backpack?.id ?? CONTAINER_EQUIPMENT;
  }

  private onGroundItems(packet: GroundItemsPacket): void {
    this.ground.update(packet);
  }

  private onEffect(packet: EffectPacket): void {
    for (const point of packet.points) {
      this.effects.playPoint(point);
      this.audio.play(soundOf(point.effectId), 120);
    }
    for (const shot of packet.shots) this.effects.playShot(shot);
  }

  private onChat(packet: ChatMessagePacket): void {
    this.chat.addSpeech(packet.channel, packet.name, packet.text);

    // Só a fala local vira balão. Um canal global desenhando balão sobre um personagem
    // que não falou — ou sobre ninguém, porque quem falou está em outro mapa — seria pior
    // que não desenhar nada.
    if (packet.channel === ChatChannel.Local) {
      this.sprites.get(packet.speakerId)?.say(packet.text);
    }
  }

  // --- Alvo ----------------------------------------------------------------

  private setupTargeting(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const point = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const tileX = Math.floor(point.x / TILE_SIZE);
      const tileY = Math.floor(point.y / TILE_SIZE);
      const clicked = this.creatureAtPoint(point.x, point.y);

      // Botão direito abre o **menu de contexto** (PRD §15.4). A gramática de clique já
      // estava saturada antes dos verbos de mundo existirem, e acrescentar cinco verbos sem
      // acrescentar como executá-los seria escrever pacotes que ninguém consegue enviar. A
      // proposta de troca, que era o antigo dono do botão direito, virou uma linha do menu.
      if (pointer.rightButtonDown()) {
        this.openContextMenu(pointer.x, pointer.y, tileX, tileY, clicked);
        return;
      }

      // Item ao alcance sob o ponteiro: o clique fica **suspenso** até o gesto se declarar.
      // Ver `armDrag` — e note que a criatura tem prioridade, porque um rato de pé sobre um
      // punhado de moedas continua sendo o que o jogador quis clicar.
      if (clicked === null && this.armDrag(pointer, tileX, tileY)) return;

      this.actOnTile(tileX, tileY, clicked);
    });

    this.setupLongPress();
    this.setupDragGesture();

    this.input.keyboard?.on('keydown-ESC', () => {
      // Esc no chat limpa o campo (ver `ChatPanel`), e não deve levar o alvo junto: quem
      // desistiu de escrever uma frase não desistiu da criatura em que estava batendo.
      if (this.chat.isTyping) return;
      // Larga o alvo e para a caminhada — um personagem que continua andando depois do Esc
      // desmente o gesto. O mesmo `Parar` do menu de toque (ver `cancelOrders`).
      this.cancelOrders();
    });
  }

  /**
   * O que um clique esquerdo faz neste tile.
   *
   * Fora do `pointerdown` porque o gesto nem sempre se resolve ali: sobre um item ao alcance,
   * o clique fica suspenso até o `pointerup` provar que não era um arraste (ver `armDrag`).
   * Uma cópia deste encadeamento em cada caminho seria a mesma decisão escrita duas vezes.
   */
  private actOnTile(tileX: number, tileY: number, clicked: number | null): void {
    const z = this.terrain?.floor ?? 0;
    const top = this.ground.topAt(tileX, tileY, z);

    // Corpo ao alcance: o clique **revista**, e é o gesto que fecha o laço do loot (PRD §6).
    //
    // Antes deste ramo, e antes até do tile do próprio jogador: pegar do chão é o que o
    // clique fazia, e sobre um corpo ele não fazia nada — o servidor recusa carregar um corpo,
    // com razão, e a recusa era a única resposta que existia. O que estava dentro dele ficava
    // inalcançável até apodrecer, inclusive o inventário de quem tinha acabado de morrer.
    if (clicked === null && top && this.reachOf(tileX, tileY) <= REACH_TILES) {
      if (itemType(top.typeId)?.kind === ItemKind.Corpse) {
        this.connection.sendUseItem(CONTAINER_GROUND, top.slot, tileX, tileY);
        return;
      }
    }

    // Clique no próprio tile pega o que está no chão. É o gesto mais direto para
    // recolher loot, e não conflita com selecionar alvo porque ninguém ataca a si.
    if (tileX === this.predictor.x && tileY === this.predictor.y) {
      this.connection.sendMoveItem({
        fromContainer: CONTAINER_GROUND,
        // A pilha do **topo**, que é a desenhada: `0` é a do fundo, e pegava uma coisa
        // enquanto o jogador via outra. Num tile com quatro pilhas, quatro cliques recolhiam
        // tudo — em ordem inversa à que a tela mostrava, sem que nada explicasse por quê.
        fromSlot: top?.slot ?? 0,
        toContainer: this.backpackId(),
        toSlot: 0,
        count: 0,
      });
      return;
    }

    // A porta abre no **clique esquerdo**, sem passar pelo menu (PRD §15.4).
    //
    // É o gesto que todo jogador tenta primeiro, e até aqui ele não fazia nada: abrir uma
    // porta exigia descobrir o botão direito, e o menu do navegador ainda tapava o do jogo
    // (ver `main.ts`). Duas descobertas empilhadas para o verbo mais básico do cenário — e
    // no celular, onde não há botão direito, sobrava só o toque longo.
    //
    // Não rouba clique de mais nada: uma porta fechada é bloqueante, então ninguém está de
    // pé nela para virar alvo, o tile do próprio jogador já saiu acima, e andar até ela é
    // impossível pela mesma razão de ela bloquear.
    if (clicked === null && this.isReachableDoor(tileX, tileY)) {
      this.connection.sendUseTile(tileX, tileY, this.terrain?.floor ?? 0);
      return;
    }

    // NPC de conversa: o mesmo clique que ataca um lobo abre a loja do mercador. A
    // distinção vem do `EntityInfo` (fixo, mandado uma vez) e não de adivinhar pelo
    // desenho — e sem ela o clique no mercador viraria um ataque recusado, que é a
    // resposta errada para o gesto certo.
    if (clicked !== null && this.friendly.has(clicked)) {
      this.connection.sendInteract(clicked);
      return;
    }

    if (clicked !== null) {
      this.selectTarget(clicked);
      return;
    }

    // Chão livre: o personagem anda até lá (PRD §6).
    //
    // Este clique **desmarcava o alvo**, e a troca é deliberada. Andar passou a ser o clique
    // mais frequente do jogo, e um gesto de reposicionar que cancela o auto-attack a cada vez
    // tornaria as duas coisas mutuamente exclusivas: ou se anda, ou se bate. Largar o alvo
    // continua a um gesto de distância — Esc, que já fazia isso e agora também para a
    // caminhada —, e a linha "alvo desmarcado" continua saindo por lá.
    this.startWalk(tileX, tileY);
  }

  /**
   * Toque longo: o equivalente do clique direito no celular (PRD §15.4).
   *
   * O movimento cancela — um toque que arrasta é o direcional, e confundir os dois faria o
   * personagem parar de andar toda vez que alguém segurasse a direção. É o mesmo motivo pelo
   * qual o menu não abre em `pointerup`: quem soltou o dedo já andou.
   */
  private setupLongPress(): void {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let startX = 0;
    let startY = 0;

    const cancel = (): void => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
    };

    this.cancelLongPress = cancel;

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      cancel();
      if (pointer.rightButtonDown()) return;

      startX = pointer.x;
      startY = pointer.y;

      timer = setTimeout(() => {
        timer = null;

        // O menu ganha do clique suspenso: o dedo parado sobre um item pediu o menu, não
        // pediu para pegá-lo. Sem soltar o arraste aqui, o `pointerup` que fecha o toque
        // longo ainda dispararia a ação de clique daquele tile, por baixo do menu aberto.
        this.drag = null;

        const point = this.cameras.main.getWorldPoint(startX, startY);
        this.openContextMenu(
          startX,
          startY,
          Math.floor(point.x / TILE_SIZE),
          Math.floor(point.y / TILE_SIZE),
          this.creatureAtPoint(point.x, point.y),
        );
      }, LONG_PRESS_MS);
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (timer === null) return;
      if (Math.hypot(pointer.x - startX, pointer.y - startY) > LONG_PRESS_SLOP_PX) cancel();
    });

    this.input.on('pointerup', cancel);
  }

  // --- Arrastar o que está no cenário ---------------------------------------

  /**
   * Suspende o clique quando há um item arrastável sob o ponteiro.
   *
   * Devolve `true` quando assumiu o gesto — e nesse caso o `pointerdown` não faz mais nada:
   * o que vai acontecer só se decide quando o dedo se mover (arraste) ou soltar (clique).
   *
   * **Só ao alcance do braço**, o mesmo `REACH` que o servidor aplica: suspender o clique
   * sobre um item a dez tiles não daria arraste nenhum — o servidor recusaria — e ainda
   * atrasaria um clique que hoje é imediato.
   */
  private armDrag(pointer: Phaser.Input.Pointer, tileX: number, tileY: number): boolean {
    const z = this.terrain?.floor ?? 0;
    const top = this.ground.topAt(tileX, tileY, z);
    if (!top) return false;

    if (this.reachOf(tileX, tileY) > REACH_TILES) return false;

    this.drag = {
      x: tileX,
      y: tileY,
      z,
      slot: top.slot,
      typeId: top.typeId,
      count: top.count,
      fromX: pointer.x,
      fromY: pointer.y,
      active: false,
    };

    return true;
  }

  /**
   * O arraste de verdade: seguir o cursor e descobrir onde ele soltou.
   *
   * Os ouvintes são **do documento**, e não do Phaser: o destino mais comum de um item pego
   * do chão é a mochila, que fica na barra lateral, fora do canvas. Phaser só entrega
   * ponteiro sobre a própria tela, então um arraste dirigido por ele perderia o item no
   * instante em que o cursor cruzasse a borda do mundo — que é justamente onde o gesto
   * termina.
   */
  private setupDragGesture(): void {
    const { signal } = this.listeners;

    window.addEventListener(
      'pointermove',
      (event) => {
        const drag = this.drag;
        if (!drag) return;

        const gameX = this.scale.transformX(event.pageX);
        const gameY = this.scale.transformY(event.pageY);

        if (!drag.active) {
          if (Math.hypot(gameX - drag.fromX, gameY - drag.fromY) <= DRAG_SLOP_PX) return;

          // Passou do limiar: é arraste. O toque longo cai junto, porque o dedo que anda já
          // não está segurando nada parado.
          this.cancelLongPress();
          drag.active = true;
          this.ghost.show(this.itemIcon(drag.typeId), drag.count, event.clientX, event.clientY);
        }

        this.ghost.moveTo(event.clientX, event.clientY);
        this.showDragTarget(event.clientX, event.clientY, gameX, gameY);
      },
      { signal },
    );

    const finish = (event: PointerEvent): void => {
      const drag = this.drag;
      this.drag = null;
      if (!drag) return;

      this.ghost.hide();
      clearDropHighlight();
      this.marks.clearHighlight();

      // Não chegou a virar arraste: era um clique, e ele acontece agora — o `pointerdown`
      // deste gesto não fez nada justamente para esperar por esta linha.
      if (!drag.active) {
        this.actOnTile(drag.x, drag.y, null);
        return;
      }

      this.dropDraggedItem(drag, event);
    };

    window.addEventListener('pointerup', finish, { signal });
    // O cancelamento do sistema — o gesto virou rolagem, a aba perdeu o toque — solta o item
    // sem entregá-lo a lugar nenhum. Perder o arraste é aborrecimento; largar o item num
    // destino que ninguém escolheu é perder o item de vista.
    window.addEventListener(
      'pointercancel',
      () => {
        this.drag = null;
        this.ghost.hide();
        clearDropHighlight();
        this.marks.clearHighlight();
      },
      { signal },
    );
  }

  /** Acende o destino sob o cursor: um slot do inventário, ou um tile do cenário. */
  private showDragTarget(
    clientX: number,
    clientY: number,
    gameX: number,
    gameY: number,
  ): void {
    const element = document.elementFromPoint(clientX, clientY);
    const target = dropTargetAt(element);

    if (target) {
      highlightDropTarget(element);
      this.marks.clearHighlight();
      return;
    }

    clearDropHighlight();

    if (!element?.closest('#viewport')) {
      this.marks.clearHighlight();
      return;
    }

    const tile = this.tileAt(gameX, gameY);
    this.marks.highlight(
      tile.x,
      tile.y,
      // A mesma pergunta que o servidor fará: cabe no braço, e cabe alguma coisa ali.
      this.reachOf(tile.x, tile.y) <= REACH_TILES &&
        (this.terrain?.isWalkable(tile.x, tile.y) ?? false),
    );
  }

  /**
   * Entrega o item arrastado ao que estiver sob o ponto em que o dedo soltou.
   *
   * Nada é decidido aqui além do endereço: peso, capacidade, alcance e slot compatível são
   * do servidor, como em todo `MoveItem` (ver `ui/inventory.ts`). O cliente que adivinhasse a
   * recusa teria duas regras de peso, e a dele estaria errada na primeira vez que a outra
   * mudasse.
   *
   * Os dois sistemas de coordenada do evento são usados, e não é descuido: `elementFromPoint`
   * quer o ponto da **janela** (`client`), e `ScaleManager.transformX` quer o do **documento**
   * (`page`), que é o que o próprio Phaser passa para ele. Os dois só coincidem enquanto nada
   * rola, e um jogo em tela cheia é exatamente o lugar onde essa coincidência dura anos antes
   * de deixar de valer.
   */
  private dropDraggedItem(drag: WorldDrag, event: PointerEvent): void {
    const element = document.elementFromPoint(event.clientX, event.clientY);
    const target = dropTargetAt(element);

    if (target?.kind === 'slot') {
      this.connection.sendMoveItem({
        fromContainer: CONTAINER_GROUND,
        fromSlot: drag.slot,
        fromX: drag.x,
        fromY: drag.y,
        toContainer: target.container,
        toSlot: target.slot,
        count: 0,
      });
      return;
    }

    if (target?.kind === 'ground') {
      this.connection.sendMoveItem({
        fromContainer: CONTAINER_GROUND,
        fromSlot: drag.slot,
        fromX: drag.x,
        fromY: drag.y,
        toContainer: CONTAINER_GROUND,
        toSlot: 0,
        count: 0,
      });
      return;
    }

    if (!element?.closest('#viewport')) return;

    const tile = this.tileAt(
      this.scale.transformX(event.pageX),
      this.scale.transformY(event.pageY),
    );
    if (tile.x === drag.x && tile.y === drag.y) return;

    this.connection.sendMoveItem({
      fromContainer: CONTAINER_GROUND,
      fromSlot: drag.slot,
      fromX: drag.x,
      fromY: drag.y,
      toContainer: CONTAINER_GROUND,
      toSlot: 0,
      toX: tile.x,
      toY: tile.y,
      count: 0,
    });
  }

  /**
   * O caminho de volta: arrastar do inventário **para** o cenário.
   *
   * Este lado usa o arraste nativo do DOM, e não o de ponteiro acima, porque a origem é um
   * elemento — o slot já dispara `dragstart` e já põe a origem no `dataTransfer`, que é como a
   * mesa de troca também a recebe (ver `ui/inventory.ts`). O canvas só precisa aceitar o
   * pacote. Dois mecanismos, um por origem, cada um usando o que o navegador já dá de graça
   * naquele lado.
   *
   * A faixa "soltar no chão" do inventário continua existindo, e continua sendo o caminho
   * fácil para largar sem mirar: ela cai no tile de baixo dos pés.
   */
  private setupWorldDrop(): void {
    const { signal } = this.listeners;
    const canvas = this.game.canvas;

    canvas.addEventListener(
      'dragover',
      (event) => {
        // Sem `preventDefault` o navegador recusa o drop e nem chega a emitir o evento.
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';

        const tile = this.tileAt(
          this.scale.transformX(event.pageX),
          this.scale.transformY(event.pageY),
        );
        this.marks.highlight(
          tile.x,
          tile.y,
          this.reachOf(tile.x, tile.y) <= REACH_TILES &&
            (this.terrain?.isWalkable(tile.x, tile.y) ?? false),
        );
      },
      { signal },
    );

    canvas.addEventListener('dragleave', () => this.marks.clearHighlight(), { signal });

    canvas.addEventListener(
      'drop',
      (event) => {
        event.preventDefault();
        this.marks.clearHighlight();

        const raw = event.dataTransfer?.getData('application/x-hexaria-item');
        if (!raw) return;

        const source = JSON.parse(raw) as { container: number; slot: number };
        const tile = this.tileAt(
          this.scale.transformX(event.pageX),
          this.scale.transformY(event.pageY),
        );

        this.connection.sendMoveItem({
          fromContainer: source.container,
          fromSlot: source.slot,
          toContainer: CONTAINER_GROUND,
          toSlot: 0,
          toX: tile.x,
          toY: tile.y,
          count: 0,
        });
      },
      { signal },
    );
  }

  /**
   * Distância deste tile ao personagem, na régua do servidor.
   *
   * Chebyshev, e contra a posição **prevista**: é dela que o desenho do personagem sai, e usar
   * a do último Snapshot faria o gesto ser recusado durante o passo (PRD §11.1).
   */
  private reachOf(tileX: number, tileY: number): number {
    return chebyshevDistance(this.predictor.x, this.predictor.y, tileX, tileY);
  }

  /** Tile sob um ponto em coordenadas de **jogo** (as do canvas, já com o zoom aplicado). */
  private tileAt(gameX: number, gameY: number): { x: number; y: number } {
    const point = this.cameras.main.getWorldPoint(gameX, gameY);
    return { x: Math.floor(point.x / TILE_SIZE), y: Math.floor(point.y / TILE_SIZE) };
  }

  /** A textura do item como imagem que o DOM consegue desenhar. Ver `setIconResolver`. */
  private itemIcon(typeId: number): string | undefined {
    const texture = this.textures.get(itemTextureKey(typeId));
    const source = texture?.getSourceImage();
    return source instanceof HTMLCanvasElement ? source.toDataURL() : undefined;
  }

  /**
   * As ações que valem para o que está sob o ponteiro.
   *
   * A lista é montada aqui, e não dentro do menu, porque quem sabe o que há naquele tile é a
   * cena: o menu recebe rótulos e funções, e não conhece o estado do mundo.
   *
   * "Olhar" está sempre presente, e de propósito: é a ação que nunca falha, e é ela que
   * ensina o jogador de que o menu serve para descobrir o resto.
   */
  private openContextMenu(
    screenX: number,
    screenY: number,
    tileX: number,
    tileY: number,
    creatureId: number | null,
  ): void {
    const z = this.terrain?.floor ?? 0;
    const actions: ContextAction[] = [];

    actions.push({
      label: 'Olhar',
      run: () => this.connection.sendLook(tileX, tileY, z),
    });

    if (creatureId !== null && creatureId !== this.localEntityId) {
      const state = this.states.get(creatureId);

      if (this.friendly.has(creatureId)) {
        actions.push({ label: 'Falar', run: () => this.connection.sendInteract(creatureId) });
      } else {
        actions.push({ label: 'Atacar', run: () => this.selectTarget(creatureId) });
      }

      if (state?.kind === EntityKind.Player) {
        actions.push({
          label: 'Propor troca',
          run: () => this.sendTrade(TradeAction.Request, creatureId),
        });
      }
    }

    // Usar o tile só aparece para o que está ao alcance do braço: oferecer "abrir" numa
    // porta a dez tiles é oferecer uma recusa.
    if (this.reachOf(tileX, tileY) <= REACH_TILES) {
      actions.push({
        // A porta é o único tile que o menu nomeia pelo verbo. "Usar" é honesto para uma
        // alavanca — não se sabe o que ela faz até puxar — e vago para uma porta, que é onde
        // o jogador está travado e procurando a palavra "abrir".
        label: this.useLabel(tileX, tileY, z),
        run: () => this.connection.sendUseTile(tileX, tileY, z),
      });
    }

    // O que está no chão deste tile, ao alcance do braço — que é o mesmo alcance do
    // servidor, e por isso o menu não oferece o que ele recusaria (ver `moveItem`).
    const top = this.ground.topAt(tileX, tileY, z);
    if (top && this.reachOf(tileX, tileY) <= REACH_TILES) {
      const corpse = itemType(top.typeId)?.kind === ItemKind.Corpse;

      actions.push({
        // "Revistar" e não "pegar": um corpo não se carrega, e oferecer o verbo errado sobre
        // ele é oferecer a recusa que o jogador já ouviu.
        label: corpse ? 'Revistar' : 'Pegar do chão',
        run: () =>
          corpse
            ? this.connection.sendUseItem(CONTAINER_GROUND, top.slot, tileX, tileY)
            : this.connection.sendMoveItem({
                fromContainer: CONTAINER_GROUND,
                fromSlot: top.slot,
                toContainer: this.backpackId(),
                toSlot: 0,
                count: 0,
                fromX: tileX,
                fromY: tileY,
              }),
      });
    }

    this.contextMenu.show(screenX, screenY, actions);
  }

  /**
   * Há uma porta aqui, e ela está ao alcance do braço? Ver `reachOf` para a régua.
   *
   * A porta trancada entra: quem clica nela ouve do servidor que falta a chave, e essa frase
   * é a única maneira de descobrir que aquela porta tem fechadura.
   */
  private isReachableDoor(tileX: number, tileY: number): boolean {
    const terrain = this.terrain;
    if (!terrain) return false;

    if (this.reachOf(tileX, tileY) > REACH_TILES) return false;

    return isDoorTile(terrain.at(tileX, tileY, terrain.floor));
  }

  /** O verbo do tile sob o ponteiro. Ver a chamada em `openContextMenu`. */
  private useLabel(tileX: number, tileY: number, z: number): string {
    const tile = this.terrain?.at(tileX, tileY, z) ?? TileType.Unknown;

    if (tile === TileType.DoorOpen) return 'Fechar';
    if (isDoorTile(tile)) return 'Abrir';
    return 'Usar';
  }

  // --- Andar por clique ------------------------------------------------------

  /**
   * O tile está livre para o personagem local?
   *
   * Terreno **e** ocupação na mesma pergunta, porque no servidor é uma pergunta só
   * (`World.canOccupy`): tile livre é o que não tem parede nem criatura viva em cima. Sem
   * a segunda metade, andar contra um Rato atravessava ele no cliente e a divergência não
   * sarava — o passo recusado não muda nada no servidor, então a entidade local não vinha
   * no Snapshot em delta e a reconciliação nunca era chamada.
   *
   * `this.states` é o que chegou pelo Snapshot, e o `floor` é o andar visível: entidade do
   * andar anterior sobrevive um tick em `states` até o Despawn, e comparar `z` é o que
   * impede que ela bloqueie um tile do andar de cima.
   *
   * Campo, e não função declarada dentro de `pollInput`: a busca de caminho e a predição
   * fazem a mesma pergunta, e uma delas roda a cada quadro. Um fecho novo por quadro seria a
   * alocação no caminho quente que §3.1 proíbe do outro lado da rede.
   */
  private readonly isFreeTile = (x: number, y: number): boolean => {
    const terrain = this.terrain;
    if (!terrain) return false;

    return (
      terrain.isWalkable(x, y) &&
      !isOccupied(this.states.values(), x, y, terrain.floor, this.localEntityId)
    );
  };

  /**
   * Traça a caminhada até o tile clicado.
   *
   * A recusa é **visível** — o anel vermelho de `PointerMarks.ping` — e é a metade que faz o
   * gesto ser aprendível: clicar do outro lado de um muro e ver o personagem parado é
   * indistinguível de um clique perdido, e depois de duas dessas o jogador conclui que andar
   * por clique não funciona neste jogo.
   *
   * O destino tem de ser um tile **livre e conhecido**. O que ainda não chegou por `MapChunk`
   * conta como parede (§3.5), então um clique além da fronteira do terreno recebido recusa —
   * o que é honesto: dali o cliente não sabe se há caminho.
   *
   * Serve os dois cliques que pedem uma caminhada, o do cenário e o do minimapa, e marca a
   * resposta nos dois lugares: o destino de um clique no mapa costuma estar fora da tela, e
   * o anel do mundo sozinho seria uma resposta desenhada onde ninguém está olhando.
   */
  private startWalk(tileX: number, tileY: number): void {
    if (!this.connected || !this.terrain) return;

    if (tileX === this.predictor.x && tileY === this.predictor.y) {
      this.walk.clear();
      return;
    }

    const found = this.walk.to(
      this.predictor.x,
      this.predictor.y,
      tileX,
      tileY,
      this.isFreeTile,
    );

    this.marks.ping(tileX, tileY, found);
    this.minimap.showPing(tileX, tileY, found);
  }

  /** Contêiner equipado, destino padrão do que se pega do chão. */
  private backpackId(): number {
    return this.lastBackpackId;
  }

  private lastBackpackId = CONTAINER_EQUIPMENT;

  /**
   * Quem está desenhado sob este ponto do mundo.
   *
   * O teste é contra o **sprite**, na posição interpolada em que ele aparece, e não contra
   * o tile de grade do último snapshot — ver `CreatureSprite.containsPoint` para o que os
   * dois erros somados faziam com o combate (PRD §11.1, item 3).
   *
   * Empate resolve pela profundidade: entre dois personagens sobrepostos, o clique pega o
   * que está na frente, que é o que o jogador acha que está clicando.
   */
  private creatureAtPoint(worldX: number, worldY: number): number | null {
    let best: number | null = null;
    let bestDepth = -Infinity;

    for (const [id, sprite] of this.sprites) {
      if (id === this.localEntityId) continue;
      if (!sprite.containsPoint(worldX, worldY)) continue;

      if (sprite.depth >= bestDepth) {
        bestDepth = sprite.depth;
        best = id;
      }
    }

    return best;
  }

  /**
   * Duplo clique num item do inventário.
   *
   * Runa e poção são o mesmo gesto para quem joga e dois pacotes diferentes no protocolo,
   * porque a runa precisa dizer **em quem**. A escolha é feita aqui, na cena, por ser aqui
   * que o alvo selecionado existe — o inventário é uma janela do DOM e não sabe para onde a
   * câmera está olhando.
   *
   * Sem alvo, a runa vai no ponto onde o próprio personagem está: é o que faz a runa de
   * explosão e a de cura funcionarem sem exigir um clique de mira que o jogador não tem
   * como saber que precisa dar.
   */
  private useFromInventory(container: number, slot: number, typeId: number): void {
    if (itemType(typeId)?.kind !== ItemKind.Rune) {
      this.connection.sendUseItem(container, slot);
      return;
    }

    const target = this.targetId ?? 0;
    const aimed = target !== 0 ? this.sprites.get(target)?.view : undefined;
    const self = this.sprites.get(this.localEntityId)?.view;

    this.connection.sendUseOn(
      container,
      slot,
      target,
      aimed?.gridX ?? self?.gridX ?? 0,
      aimed?.gridY ?? self?.gridY ?? 0,
    );
  }

  /** Atalho para os cinco gestos da troca, que compartilham o mesmo pacote. */
  private sendTrade(
    action: TradeAction,
    targetId = 0,
    container = 0,
    slot = 0,
    count = 0,
  ): void {
    this.connection.sendTrade({ action, targetId, container, slot, count });
  }

  private selectTarget(id: number | null): void {
    if (this.targetId === id) return;

    if (this.targetId !== null) this.sprites.get(this.targetId)?.setTargeted(false);

    // Perder o alvo cancela o auto-attack, e isso acontecia sem nada na tela: o jogador
    // errava o clique por 16 px, o ataque parava, e a leitura era "o combate quebrou".
    // O gesto continua valendo — largar um alvo não pode exigir matá-lo —, mas agora ele
    // se anuncia.
    if (this.targetId !== null && id === null) {
      this.chat.addSystem('alvo desmarcado.');
    }

    this.targetId = id;
    if (id !== null) this.sprites.get(id)?.setTargeted(true);

    this.connection.sendAttack(id ?? NO_TARGET);
  }

  // --- Render --------------------------------------------------------------

  private ensureSprite(entity: EntityState): CreatureSprite {
    const existing = this.sprites.get(entity.id);
    if (existing) return existing;

    const isLocal = entity.id === this.localEntityId;
    const sprite = new CreatureSprite(this, entity, isLocal);

    const name = this.names.get(entity.id);
    if (name) sprite.setName(name);

    this.sprites.set(entity.id, sprite);

    if (isLocal) {
      this.predictor.reset(entity.x, entity.y);
      // Primeiro snapshot da região: é aqui que se descobre onde o personagem nasceu, e a
      // janela de terreno precisa saber antes do primeiro quadro desenhado.
      this.terrain?.follow(entity.x, entity.y);
      this.cameras.main.startFollow(sprite.cameraTarget, true, 0.15, 0.15);
    }

    // Reentrou na Área de Interesse ainda marcado: reacende o anel.
    if (this.targetId === entity.id) sprite.setTargeted(true);

    return sprite;
  }

  override update(time: number, deltaMs: number): void {
    // Antes da trava de terreno: a água e o portal continuam vivos mesmo entre regiões.
    animateTileset(this, time);

    if (!this.terrain) return;

    this.predictor.update(deltaMs);
    this.pollInput();

    // Depois de `pollInput`, que pode ter mudado a grade do personagem local, e antes do
    // laço, porque é o `sync` de cada sprite que consome o degrau — invertida, a ordem
    // custaria um quadro de atraso e o nome piscaria por cima do vizinho a cada passo.
    layoutCreatureLabels(this.sprites.values());

    for (const sprite of this.sprites.values()) {
      sprite.view.update(deltaMs);
      sprite.updateAnimation();
      sprite.sync();
    }

    this.hotkeys.update();

    this.pingAccumulatorMs += deltaMs;
    if (this.pingAccumulatorMs >= PING_INTERVAL_MS) {
      this.pingAccumulatorMs = 0;
      this.connection.sendPing();
    }

    this.updateSurroundings();
    this.renderHud();
  }

  /**
   * Minimapa e ambiente sonoro, os dois presos à posição de **grade**.
   *
   * Nenhum dos dois precisa de quadro: o terreno é estático e o passo leva 150ms, então
   * redesenhar o minimapa a 60fps seria repetir o mesmo desenho dez vezes. Os dois métodos
   * saem cedo quando nada mudou, e é por isso que podem morar no laço sem custar nada.
   */
  private updateSurroundings(): void {
    if (!this.terrain) return;

    const { x, y } = this.predictor;
    const z = this.terrain.floor;

    // Antes do minimapa: a janela de terreno rola aqui, e só quando o jogador entra na
    // faixa de um chunk junto à borda dela (ver `terrain-window.ts`). Nas outras noventa e
    // nove vezes em cem esta chamada são duas comparações.
    this.terrain.follow(x, y);

    this.minimap.draw(this.terrain, x, y, z, [...this.states.values()], this.localEntityId);

    // O ambiente vem do **chão sob os pés**, e não do nome da região: um mapa criado por
    // outra pessoa não tem por que se chamar "Caverna" para soar como uma.
    const tile = this.terrain.at(x, y, z);
    this.audio.setAmbience(tile === TileType.CaveFloor);
  }

  private renderHud(): void {
    this.hud.setDiagnostics({
      connected: this.connected,
      pingMs: this.connection.latencyMs,
      tick: this.serverTick,
      x: this.predictor.x,
      y: this.predictor.y,
      entities: this.sprites.size,
      corrections: this.corrections,
    });

    const target = this.targetId === null ? undefined : this.states.get(this.targetId);

    this.hud.setTarget(
      target
        ? {
            id: target.id,
            name:
              this.names.get(target.id) ??
              (target.kind === EntityKind.Npc ? 'criatura' : 'jogador'),
            health: target.health,
            maxHealth: target.maxHealth,
          }
        : null,
    );
  }

  /**
   * O passo da perseguição: ir atrás do alvo selecionado até estar ao alcance de golpeá-lo
   * (PRD §5, postura ofensiva — ver `CombatStance`).
   *
   * ## Por que ela mora no cliente
   *
   * Pela mesma razão que o clique no chão mora (ver `input/walk-path.ts`): **não existe
   * pacote de "ande até ali"**, e não deve existir. O servidor valida passo a passo, e um
   * caminho inteiro decidido lá dentro seria um segundo caminho de movimento — com o worker
   * traçando A\* por jogador por tick, que é o custo que §15.1 cortou da IA. Aqui a
   * perseguição é o que sempre foi do cliente: uma fila de `Move 0x01`, um por passo,
   * indistinguível do teclado para quem recebe.
   *
   * ## Por que ela recalcula a cada passo
   *
   * Porque o destino anda. Um caminho traçado uma vez apontaria para onde o lobo **estava**,
   * e a perseguição terminaria num tile vazio — o defeito clássico deste recurso. Uma busca
   * por passo custa umas sete por segundo, com teto próprio e bem menor que o do clique
   * (`MAX_CHASE_NODES`), justamente porque ela se repete.
   *
   * ## Onde ela para
   *
   * No **alcance da arma** e não colado no alvo: `findPathNear` recebe `attackRange`, que
   * chega do servidor no `Stats`. É o mesmo número que decide se o golpe sai lá dentro, então
   * o personagem para exatamente onde passa a acertar — e o Paladin não caminha para dentro
   * do corpo a corpo que a vocação dele existe para evitar.
   *
   * Não há aviso de "não consigo chegar": perseguir é uma consequência da postura, e não um
   * gesto que o jogador acabou de fazer. Uma linha no chat por criatura do outro lado de um
   * muro seria ruído no lugar onde ele está lendo o dano.
   */
  private chaseStep(): Direction | null {
    if (!STANCES[this.stance].chase) return null;
    if (this.targetId === null || !this.terrain) return null;

    const target = this.states.get(this.targetId);
    if (!target) return null;

    // Alvo noutro andar é alvo que o servidor já não golpeia (ver `updateCombat`), e a
    // busca é plana: perseguir aqui faria o personagem andar até debaixo de quem está no
    // andar de cima e ficar parado ali.
    if (target.z !== this.terrain.floor) return null;

    const steps = findPathNear(
      this.predictor.x,
      this.predictor.y,
      target.x,
      target.y,
      this.attackRange,
      this.isFreeTile,
    );

    // Lista vazia é "já estou ao alcance": ficar parado batendo é o comportamento certo, e
    // continuar avançando faria o personagem circular o alvo em vez de golpeá-lo — o mesmo
    // motivo pelo qual a IA para de andar em `chase` (`systems/ai.ts`).
    return steps?.[0] ?? null;
  }

  private pollInput(): void {
    // Digitando no chat, o teclado é do chat: sem esta trava, escrever "was" faria o
    // personagem correr pela tela.
    if (this.chat.isTyping) return;

    // Conexão caída: nada do que sair daqui chega a lugar nenhum, e prever passos num
    // mundo que parou de responder move o personagem — e a câmera — por um cenário morto.
    if (!this.connected) {
      this.walk.clear();
      return;
    }

    // O cooldown é checado ANTES de ler a direção, e a ordem não é estética: `take()`
    // consome o toque bufferizado. Lê-lo durante a caminhada jogaria fora exatamente o
    // comando que o buffer existe para preservar.
    if (!this.predictor.canStep) return;

    // Janela do buffer = um passo: cobre um toque perdido em qualquer ponto da
    // caminhada, sem deixar um comando velho disparar um passo fantasma depois. Teclado e
    // direcional de toque saem os dois daqui.
    const typed = this.controller.take(this.predictor.stepDurationMs);

    // A tecla vence o caminho, e o cancela. Quem tocou uma direção retomou o volante — deixar
    // o caminho vivo por baixo faria o personagem voltar sozinho ao rumo do último clique
    // assim que a tecla fosse solta, que é o defeito clássico deste par de controles.
    if (typed !== null) this.walk.clear();

    // A perseguição é a **última** da fila: tecla, depois clique, depois ela. Quem apertou uma
    // seta ou clicou num tile disse para onde quer ir, e uma postura que arrastasse o
    // personagem de volta para o alvo no meio disso seria o painel desmentindo o teclado. Ela
    // retoma sozinha assim que a caminhada pedida termina — que é como se atravessa um vão
    // para o lado seguro sem largar o alvo.
    const direction =
      typed ??
      this.walk.next(this.predictor.x, this.predictor.y, this.isFreeTile) ??
      this.chaseStep();
    if (direction === null) return;

    const free = this.isFreeTile;
    const willStep = this.predictor.canWalk(direction, free);
    const turning = direction !== this.predictor.direction;

    // O frame de Move sai quando o passo é possível, ou quando este é o quadro em que o
    // personagem **muda de direção** — virar de frente para uma parede continua valendo,
    // porque é o que decide para onde sai a próxima onda de fogo.
    //
    // O que não sai mais é o Move repetido contra um destino que o mapa local já recusa.
    // Ele custava caro duas vezes: era a metade da rajada que derrubava a conexão por
    // rate limit, e era a origem das correções em massa depois de uma travessia de região
    // — terreno ainda não recebido conta como bloqueado (§3.5), então o cliente se recusava
    // a prever, o servidor andava mesmo assim e o personagem era puxado a cada passo.
    const seq = willStep || turning ? this.connection.sendMove(direction) : 0;

    const localSprite = this.sprites.get(this.localEntityId);
    const stepped = this.predictor.applyLocalStep(direction, seq, free);
    // O passo soa na **predição**, e não na confirmação do servidor: som que chega 40ms
    // depois do movimento é lido como atraso, e o ponto do efeito é justamente confirmar
    // que o comando foi aceito.
    if (stepped) this.audio.play(Sfx.Step, 80);

    localSprite?.setDirection(direction);

    if (stepped && localSprite) {
      localSprite.view.moveTo(
        this.predictor.x,
        this.predictor.y,
        this.predictor.stepDurationMs,
      );
    }
  }

}

function requireElement(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`elemento #${id} não existe no HTML`);
  return element;
}

/**
 * Preferência guardada no navegador.
 *
 * `localStorage` lança quando o armazenamento está bloqueado — navegação privada, cookies
 * de terceiros barrados, política corporativa. Uma preferência de zoom que não persiste é
 * um aborrecimento; uma exceção não tratada aqui derruba a entrada no jogo.
 */
function readSetting(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSetting(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* sem armazenamento: a escolha vale só nesta sessão */
  }
}

/**
 * Som de um efeito visual.
 *
 * Mapeia a **família** do efeito, e não cada id: fogo, gelo e energia soam diferente entre
 * si, e uma bola de fogo soa igual a uma onda de fogo. Um som por magia daria vinte timbres
 * que ninguém distingue no meio de uma briga — e a informação que o áudio precisa passar é
 * "que tipo de coisa me acertou", não "qual das quatro".
 */
function soundOf(effectId: number): Sfx {
  switch (effectId) {
    case EffectId.Fire:
      return Sfx.Fire;
    case EffectId.Ice:
      return Sfx.Ice;
    case EffectId.Energy:
      return Sfx.Energy;
    case EffectId.Heal:
      return Sfx.Heal;
    case EffectId.Poison:
    case EffectId.Bleed:
      return Sfx.Loss;
    default:
      return Sfx.Ui;
  }
}
